/**
 * Utility functions for image processing
 */

/**
 * Resizes an image file maintaining aspect ratio with max dimension constraint
 * @param {File} file - The image file to resize
 * @param {number} maxSize - Maximum dimension (width or height) in pixels
 * @param {number} quality - Image quality (0-1, default 0.9)
 * @returns {Promise<File>} - Resized image file
 */
export const resizeImage = (file, maxSize = 590, quality = 0.9) => {
  return new Promise((resolve, reject) => {
    // Validate input
    if (!file || !file.type.startsWith('image/')) {
      reject(new Error('Invalid image file'))
      return
    }

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()

    img.onload = () => {
      try {
        const { width: originalWidth, height: originalHeight } = img

        // If image is already smaller than maxSize, don't upscale
        if (originalWidth <= maxSize && originalHeight <= maxSize) {
          canvas.width = originalWidth
          canvas.height = originalHeight
          ctx.drawImage(img, 0, 0)
        } else {
          // Calculate new dimensions maintaining aspect ratio
          let newWidth, newHeight

          if (originalWidth > originalHeight) {
            // Width is the larger dimension
            newWidth = maxSize
            newHeight = (originalHeight * maxSize) / originalWidth
          } else {
            // Height is the larger dimension (or they're equal)
            newHeight = maxSize
            newWidth = (originalWidth * maxSize) / originalHeight
          }

          // Set canvas to the calculated dimensions
          canvas.width = Math.round(newWidth)
          canvas.height = Math.round(newHeight)

          // Draw the resized image (no background fill needed)
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        }

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Create a new File object with the same name and type
              const resizedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              })
              resolve(resizedFile)
            } else {
              reject(new Error('Failed to create resized image'))
            }
          },
          file.type,
          quality
        )
      } catch (error) {
        reject(new Error(`Error processing image: ${error.message}`))
      }
    }

    img.onerror = () => {
      reject(new Error('Failed to load image'))
    }

    // Load the image
    img.src = URL.createObjectURL(file)
  })
}

/**
 * Validates image file size and type
 * @param {File} file - The image file to validate
 * @param {number} maxSizeInMB - Maximum file size in MB (default 5MB)
 * @param {string[]} allowedTypes - Allowed MIME types
 * @returns {boolean} - Whether the file is valid
 */
export const validateImageFile = (
  file,
  maxSizeInMB = 5,
  allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
) => {
  if (!file) return false

  // Check file type
  if (!allowedTypes.includes(file.type)) {
    throw new Error(`Invalid file type. Allowed types: ${allowedTypes.join(', ')}`)
  }

  // Check file size
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024
  if (file.size > maxSizeInBytes) {
    throw new Error(`File size too large. Maximum size: ${maxSizeInMB}MB`)
  }

  return true
}

/**
 * Creates a preview URL for an image file
 * @param {File} file - The image file
 * @returns {string} - Object URL for preview
 */
export const createImagePreview = (file) => {
  return URL.createObjectURL(file)
}

/**
 * Revokes a preview URL to free up memory
 * @param {string} url - The object URL to revoke
 */
export const revokeImagePreview = (url) => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url)
  }
}

/**
 * Resizes an image file to specific fixed dimensions
 * @param {File} file - The image file to resize
 * @param {number} width - Target width in pixels
 * @param {number} height - Target height in pixels
 * @param {number} quality - Image quality (0-1, default 0.9)
 * @returns {Promise<File>} - Resized image file
 */
export const resizeImageToFixedDimensions = (file, width, height, quality = 0.9) => {
  return new Promise((resolve, reject) => {
    // Validate input
    if (!file || !file.type.startsWith('image/')) {
      reject(new Error('Invalid image file'))
      return
    }

    if (!width || !height || width <= 0 || height <= 0) {
      reject(new Error('Invalid dimensions provided'))
      return
    }

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()

    img.onload = () => {
      try {
        // Set canvas to the target dimensions
        canvas.width = width
        canvas.height = height

        // Draw the image scaled to fit the canvas exactly
        ctx.drawImage(img, 0, 0, width, height)

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Create a new File object with the same name and type
              const resizedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              })
              resolve(resizedFile)
            } else {
              reject(new Error('Failed to create resized image'))
            }
          },
          file.type,
          quality
        )
      } catch (error) {
        reject(new Error(`Error processing image: ${error.message}`))
      }
    }

    img.onerror = () => {
      reject(new Error('Failed to load image'))
    }

    // Load the image
    img.src = URL.createObjectURL(file)
  })
}
